import { Check, X, Info } from 'lucide-react';
import { motion } from 'framer-motion';
import SplashMobileView from './SplashMobileView';
import { useState } from 'react';

const THEME_COLOR = '#c59253';

const SplashConfig = ({ state, updateState }) => {
    const [splashEnabled, setSplashEnabled] = useState(state.splashData?.enabled ?? null);
    const [redirectToMenu, setRedirectToMenu] = useState(!!state.splashData?.menu_url);

    const handleFieldChange = (field, value) => {
        updateState({
            splashData: {
                ...state.splashData,
                [field]: value,
            },
        });

    };

    const handleEnableSplash = (enable) => {
        setSplashEnabled(enable);
        handleFieldChange('enabled', enable);
    };

    // Flatten items from sections as state.items is deprecated
    const allItems = (state.sections || []).flatMap(section => section.items || []);
    const serviceItems = allItems.filter(item => item.type === 'service');

    if (state.businessType !== 'restaurant') {
        return null;
    }

    if (splashEnabled === null) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center p-4">
                <div className="container" style={{ maxWidth: '700px' }}>
                    <div className="text-center mb-5">
                        <h1 className="h2 fw-bold mb-3">Do you want a splash screen?</h1>
                        <p className="text-muted">
                            A splash screen is the first thing customers see when they open your menu.
                        </p>
                    </div>

                    <div className="row g-4">
                        <div className="col-12 col-sm-6">
                            <button
                                onClick={() => handleEnableSplash(true)}
                                className="w-100 text-start p-4 rounded-4 border-2 border bg-white hover-shadow transition-all"
                                style={{ borderColor: '#dee2e6' }}
                            >
                                <div className="d-flex align-items-center justify-content-center rounded-circle mb-4" style={{ width: '48px', height: '48px', backgroundColor: THEME_COLOR }}>
                                    <Check className="text-white" size={24} />
                                </div>
                                <h5 className="fw-bold mb-2">Yes, add splash screen</h5>
                                <p className="text-muted small mb-0">
                                    Create a welcoming first impression and highlight services.
                                </p>
                            </button>
                        </div>

                        <div className="col-12 col-sm-6">
                            <button
                                onClick={() => handleEnableSplash(false)}
                                className="w-100 text-start p-4 rounded-4 border-2 border bg-white hover-shadow transition-all"
                                style={{ borderColor: '#dee2e6' }}
                            >
                                <div className="d-flex align-items-center justify-content-center bg-light rounded-circle mb-4" style={{ width: '48px', height: '48px' }}>
                                    <X className="text-dark" size={24} />
                                </div>
                                <h5 className="fw-bold mb-2">No, skip it</h5>
                                <p className="text-muted small mb-0">
                                    Go directly to the menu list.
                                </p>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!splashEnabled) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center p-4">
                <div className="text-center">
                    <div className="d-flex align-items-center justify-content-center bg-light rounded-circle mx-auto mb-4" style={{ width: '64px', height: '64px' }}>
                        <Check size={32} className="text-dark" />
                    </div>
                    <h2 className="h4 fw-bold mb-2">Splash screen disabled</h2>
                    <p className="text-muted mb-4">Customers will see the menu directly.</p>
                    <button
                        onClick={() => setSplashEnabled(null)}
                        className="btn btn-sm btn-outline-secondary rounded-pill px-4"
                    >
                        Change selection
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="h-100 d-flex flex-column flex-lg-row overflow-hidden">
            {/* Left: Form */}
            <div className="flex-grow-1 p-4 p-lg-5 overflow-auto bg-white">
                <div className="mx-auto" style={{ maxWidth: '650px' }}>
                    <button
                        onClick={() => setSplashEnabled(null)}
                        className="btn btn-link text-muted mb-3 p-0 text-decoration-none small"
                    >
                        ← Back to choice
                    </button>

                    <h1 className="h3 fw-bold mb-2">Configure Splash Screen</h1>
                    <p className="text-muted mb-5">Customize titles and button actions.</p>

                    <div className="d-flex flex-column gap-5 pb-5">
                        {/* Basic Info */}
                        <div className="row g-4">
                            <div className="col-12 col-md-6">
                                <label className="form-label small fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Title (Optional)</label>
                                <input
                                    type="text"
                                    value={state.splashData?.title || ''}
                                    onChange={(e) => handleFieldChange('title', e.target.value)}
                                    className="form-control border-2 shadow-none rounded-3"
                                    placeholder="e.g. Space: {Space Name}"
                                />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label small fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Subtitle</label>
                                <input
                                    type="text"
                                    value={state.splashData?.subtitle || ''}
                                    onChange={(e) => handleFieldChange('subtitle', e.target.value)}
                                    className="form-control border-2 shadow-none rounded-3"
                                    placeholder="Choose and Order ... We will bring it to your table."
                                />
                            </div>
                        </div>

                        {/* Buttons Configuration */}
                        <div className="card border-0 bg-light rounded-4 p-4">
                            <h6 className="fw-bold mb-4 d-flex align-items-center gap-2">
                                <div className="bg-white rounded p-1 shadow-sm"><Check size={16} color={THEME_COLOR} /></div>
                                Button Configuration
                            </h6>
                            <div className="row g-4 mb-4">
                                <div className="col-12 col-md-6">
                                    <label className="form-label small fw-bold text-muted">Menu Button Caption</label>
                                    <input
                                        type="text"
                                        value={state.splashData?.menu_button_caption || ''}
                                        onChange={(e) => handleFieldChange('menu_button_caption', e.target.value)}
                                        className="form-control border-0 shadow-sm rounded-3"
                                        placeholder="Check menu"
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <label className="form-label small fw-bold text-muted">Action Button Caption</label>
                                    <input
                                        type="text"
                                        value={state.splashData?.action__button_text || ''}
                                        onChange={(e) => handleFieldChange('action__button_text', e.target.value)}
                                        className="form-control border-0 shadow-sm rounded-3"
                                        placeholder="Call waiter"
                                    />
                                </div>
                            </div>

                            <div className="form-check form-switch mb-3">
                                <input
                                    className="form-check-input shadow-none"
                                    type="checkbox"
                                    role="switch"
                                    id="redirectSwitch"
                                    checked={redirectToMenu}
                                    onChange={(e) => {
                                        setRedirectToMenu(e.target.checked);
                                        if (!e.target.checked) handleFieldChange('menu_url', '');
                                    }}
                                />
                                <label className="form-check-label small fw-medium text-dark ms-2" htmlFor="redirectSwitch">Redirect to Menu URL</label>
                            </div>

                            {redirectToMenu && (
                                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                                    <label className="form-label small fw-bold text-muted">Menu URL</label>
                                    <input
                                        type="text"
                                        value={state.splashData?.menu_url || ''}
                                        onChange={(e) => handleFieldChange('menu_url', e.target.value)}
                                        className="form-control border-0 shadow-sm rounded-3 px-3 py-2"
                                        placeholder="https://example.com/menu"
                                    />
                                </motion.div>
                            )}
                        </div>

                        {/* Action Service Configuration */}
                        <div className="card border-0 bg-light rounded-4 p-4">
                            <h6 className="fw-bold mb-4 d-flex align-items-center gap-2">
                                <div className="bg-white rounded p-1 shadow-sm"><Info size={16} color={THEME_COLOR} /></div>
                                Action Service Configuration
                            </h6>

                            <div className="mb-4">
                                <label className="form-label small fw-bold text-muted">Action Type</label>
                                <select
                                    value={state.splashData?.service_action_type || (state.splashData?.menu_item_id ? 'custom' : 'none')}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === 'CALL_WAITER') {
                                            handleFieldChange('service_action_type', 'CALL_WAITER');
                                            handleFieldChange('menu_item_id', null);
                                            handleFieldChange('action__button_text', 'Call waiter');
                                        } else if (val === 'none') {
                                            handleFieldChange('service_action_type', null);
                                            handleFieldChange('menu_item_id', null);
                                            handleFieldChange('action__button_text', null);
                                        } else {
                                            handleFieldChange('service_action_type', null);
                                            if (!state.splashData?.action__button_text) {
                                                handleFieldChange('action__button_text', 'Order');
                                            }
                                        }
                                    }}
                                    className="form-select border-0 shadow-sm rounded-3"
                                >
                                    <option value="none">None (Standard View)</option>
                                    <option value="CALL_WAITER">Call Waiter (Recommended)</option>
                                    <option value="custom">Custom Service Item</option>
                                </select>
                            </div>

                            {(state.splashData?.service_action_type || state.splashData?.menu_item_id || (state.splashData?.service_action_type === null && state.splashData?.menu_item_id !== undefined)) && (
                                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-4">
                                    <label className="form-label small fw-bold text-muted">Action Button Caption</label>
                                    <input
                                        type="text"
                                        value={state.splashData?.action__button_text || ''}
                                        onChange={(e) => handleFieldChange('action__button_text', e.target.value)}
                                        className="form-control border-0 shadow-sm rounded-3 px-3 py-2"
                                        placeholder={state.splashData?.service_action_type === 'CALL_WAITER' ? 'Call waiter' : 'Order'}
                                    />
                                </motion.div>
                            )}

                            {(state.splashData?.menu_item_id !== undefined && state.splashData?.service_action_type !== 'CALL_WAITER') && (
                                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-4">
                                    <label className="form-label small fw-bold text-muted">Highlight Menu Item</label>
                                    <select
                                        value={state.splashData?.menu_item_id || ''}
                                        onChange={(e) => handleFieldChange('menu_item_id', e.target.value)}
                                        className="form-select border-0 shadow-sm rounded-3"
                                    >
                                        <option value="">Select a service item...</option>
                                        {serviceItems.length > 0 && <optgroup label="Service Items">
                                            {serviceItems.map((item) => (
                                                <option key={item.id} value={item.id}>
                                                    {item.name}
                                                </option>
                                            ))}
                                        </optgroup>}
                                    </select>
                                </motion.div>
                            )}

                            <div>
                                <label className="form-label small fw-bold text-muted">Action Note (Optional)</label>
                                <textarea
                                    className="form-control border-0 shadow-sm rounded-3"
                                    rows={2}
                                    value={state.splashData?.note || ''}
                                    onChange={(e) => handleFieldChange('note', e.target.value)}
                                    placeholder="Type a note for the selected service..."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right: Live Preview - Hidden on Mobile */}
            <div className="d-none d-lg-flex col-lg-5 bg-light p-4 p-lg-5 border-start" style={{ flexDirection: 'column', overflow: 'hidden' }}>
                <h3 className="text-center text-muted small text-uppercase fw-bold mb-4 flex-shrink-0">Live Preview</h3>
                <div className="flex-grow-1 d-flex align-items-center justify-content-center" style={{ overflow: 'hidden' }}>
                    <div className="border rounded-4 bg-white shadow-lg overflow-hidden" style={{ width: '320px', height: '100%', maxHeight: '640px' }}>
                        <SplashMobileView state={state} />
                    </div>
                </div>
            </div>
        </div >
    );
};

export default SplashConfig;
